import { useEffect, useState } from "react";
import { ChangeEvent } from "react";

import {
  ArrowButton,
  AutocompleteMultipleSelect,
  Checkbox,
  Select,
  TableMetadata,
  TableStateWithFilters,
  Typography,
  useTable,
} from "@king-ict/eupisi-ui/components";
import {
  CodeBook,
  ExportTypeEnum,
  PagedResult,
} from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import {
  createInitialInstitutionSearch,
  createInitialTableState,
  isInstitutionClaimType,
} from "./utils";
import { searchTowns } from "../../api/Geography/GeographyApi";
import {
  getInstitutionListData,
  searchInstitutionFilteredNew,
  searchInstitutionNew,
} from "../../api/Institution/InstitutionApi";
import {
  ContentArea,
  CountySelect,
  ListTable,
  SearchCard,
  ToolbarTop,
} from "../../components";
import { useAuth } from "../../hooks/auth/useAuth";
import { useFilterPersistence } from "../../hooks/useFilterPersistence";
import {
  Institution,
  InstitutionSearch,
  InstitutionSort,
  InstitutionCodeBookSearch,
  InstitutionCodeBook,
} from "../../models/Institution";
import {
  GrandParentCodeBookPagedFilter,
  HierarchicalCodeBook,
} from "../../models/Shared";
import { ClaimType } from "../../models/User";
import { useGetProgramsQuery } from "../../queries/codeBookQuery";
import { institutionDetailsUrl } from "../../routes/routes";
import { TableUtils } from "../../utils";

//STORAGE KEY
const storageKey = "InstitutionListSearchTableState";

export function InstitutionList() {
  const { t } = useTranslation();
  const history = useHistory();
  const auth = useAuth();

  const [initialTableState] = useState<
    TableStateWithFilters<InstitutionSearch>
  >(() => {
    const savedState = createInitialTableState(storageKey);

    // If there's saved filter data in session storage, use it
    const savedFilters = sessionStorage.getItem(storageKey);
    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters);
      return {
        ...savedState,
        filters: parsedFilters,
      };
    }

    return savedState;
  });

  const defaultInstitutionIds =
    isInstitutionClaimType(auth.currentRole?.claimType) &&
    (auth.currentRole?.locations?.length ?? 0) > 0 &&
    auth.currentRole?.locations
      ? auth.currentRole.locations
      : ([] as number[]);
  //TABLE DATA
  const defaultTableData = TableUtils.tableDefaultData<Institution>();
  const [tableData, setTableData] =
    useState<PagedResult<Institution>>(defaultTableData);
  //SET SEARCH FILTERS
  const {
    filterState: institutionSearch,
    setFilterState: setInstitutionSearch,
    saveFilters,
    clearFilters,
    filterChanged,
    setClearFilter,
  } = useFilterPersistence<InstitutionSearch>({
    storageKey,
    initialState: () =>
      createInitialInstitutionSearch(
        auth.currentRole,
        initialTableState,
        defaultInstitutionIds
      ),
    onRestore: async (savedFilters) => {
      if (savedFilters.townIds?.length > 0) {
        await handleTownFilterChange("");
      }
      if (savedFilters.institutionIds?.length > 0) {
        await handleInstitutionFilterChange("");
      }
    },
  });

  //TABLE METADATA
  const tableMetadata: TableMetadata<Institution> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "code",
        header: t("common.institutionList.programCode"),
        accessor: "code",
        sortBy: InstitutionSort.Code,
      },
      {
        columnKey: "town",
        header: t("common.institutionList.townName"),
        accessor: (item: Institution) => {
          return <div>{item.address?.town}</div>;
        },
        sortBy: InstitutionSort.Town,
      },
      {
        columnKey: "institutionName",
        header: t("common.institutionList.institutionName"),
        accessor: "name",
        sortBy: InstitutionSort.Institution,
      },
      {
        columnKey: "institutionAddress",
        header: t("common.institutionList.institutionAddress"),
        accessor: (item: Institution) => {
          return <div>{item.address?.street}</div>;
        },
        sortBy: InstitutionSort.AdresaSjedista,
      },
      {
        columnKey: "program",
        header: t("common.institutionList.programName"),
        accessor: (item: Institution) => {
          return (
            <ul>
              {item.programs.map((oneProgram) => (
                <li>
                  <Typography component="span" variant="small">
                    {oneProgram.name}
                  </Typography>
                </li>
              ))}
            </ul>
          );
        },
        sortBy: InstitutionSort.Program,
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item: Institution) => {
          return (
            <div className={"text-right"}>
              <ArrowButton
                ariaLabel={t("common.institutionList.details")}
                onClick={() => handleDetailsClicked(item.id)}
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };
  //SET TABLE
  const [tableProps, tableState, setFilters] = useTable<InstitutionSearch>({
    metadata: tableMetadata,
    data: tableData!.results,
    pagination: true,
    paginationPage: initialTableState.page,
    paginationPageSize: initialTableState.pageSize as any,
    totalItems: tableData!.rowCount,
    defaultSort: {
      order: initialTableState.order,
      sortBy: initialTableState.sortBy,
    },
  });
  //FILTER DATA
  const [filteredTowns, setFilteredTowns] = useState<CodeBook[]>(
    [] as CodeBook[]
  );
  const [filteredInstitutions, setFilteredInstitutions] = useState<
    InstitutionCodeBook[]
  >([]);
  const { data: programs } = useGetProgramsQuery();

  useEffect(() => {
    (async () => {
      const storage = sessionStorage.getItem(storageKey);

      if (storage) {
        const parsedStorage = JSON.parse(storage);

        // Load saved towns if any
        if (parsedStorage.townIds?.length > 0) {
          await handleTownFilterChange("");
        }

        // Load saved institutions if any
        if (parsedStorage.institutionIds?.length > 0) {
          await handleInstitutionFilterChange("");
        }
      } else if (institutionSearch.institutionIds?.length > 0) {
        await handleInstitutionFilterChange("");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      const request = TableUtils.GetSearchRequest<InstitutionSearch>(
        institutionSearch,
        tableState
      );
      const itemList = await searchInstitutionFilteredNew(request);
      if (itemList !== null && itemList.results.length > 0) {
        setTableData(itemList);
      } else {
        setTableData(defaultTableData);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  const handleDetailsClicked = (id?: number) => {
    if (!id) return;

    const {
      countyId,
      townIds,
      institutionIds,
      programIds,
      isMinority,
      isReligious,
    } = institutionSearch;

    const filterState = {
      ...institutionSearch,
      countyId,
      townIds,
      institutionIds,
      programIds,
      isMinority,
      isReligious,
    };

    saveFilters(filterState);
    history.push(institutionDetailsUrl(id!));
  };

  const handleDownloadClicked = async (exportType: ExportTypeEnum) => {
    await getInstitutionListData(institutionSearch, exportType);
  };

  const search = () => {
    setFilters({ ...institutionSearch });
  };

  const handleCountyChange = (e: any) => {
    const countyId = Number.parseInt(e.target.value);

    const newInstitutionSearch: InstitutionSearch = {
      ...institutionSearch,
      countyId: !isNaN(countyId) ? countyId : undefined,
    };
    setInstitutionSearch(newInstitutionSearch);
  };
  const handleTownFilterChange = async (filter: string) => {
    const searchTown: GrandParentCodeBookPagedFilter = {
      pageSize: 500,
      page: 1,
      ids: institutionSearch.townIds,
      search: filter,
      grandParentId: institutionSearch.countyId
        ? [institutionSearch.countyId]
        : [],
    };
    const result = await searchTowns(searchTown);
    if (result?.results) setFilteredTowns(result.results);
  };

  const handleInstitutionFilterChange = async (filter: string) => {
    const search: InstitutionCodeBookSearch = {
      search: filter,
      onlyActive: false,
      includeOutsideEnrollmentArea: true,
      includeHandicapped: undefined, // both
      includePrivate: undefined, // both
      includeSatellite: undefined, // both
      ids:
        auth.currentRole?.claimType === ClaimType.Institution
          ? auth.currentRole.locations
          : institutionSearch.institutionIds,
      townIds: institutionSearch.townIds,
      countyIds: institutionSearch.countyId ? [institutionSearch.countyId] : [],
    };

    const result = await searchInstitutionNew(search);
    setFilteredInstitutions(result);
  };
  const handleTownChange = (e: any) => {
    const newTowns = e.target.value as CodeBook[];
    const newInstitutions = filteredInstitutions
      .filter(
        (val) =>
          institutionSearch.institutionIds.find((v) => val.id === v) !==
          undefined
      )
      .filter((val) => newTowns.find((v) => v.id === val.townId) !== undefined);

    const newInstitutionSearch: InstitutionSearch = {
      ...institutionSearch,
      townIds: newTowns ? newTowns.map((val) => val.id) : [],
      institutionIds: newInstitutions
        ? newInstitutions.map((val) => val.id)
        : [],
    };
    setInstitutionSearch(newInstitutionSearch);
  };
  const handleProgramChange = (e: any) => {
    const newPrograms = e.target.value as number[];

    const newInstitutionSearch: InstitutionSearch = {
      ...institutionSearch,
      programIds: newPrograms,
    };
    setInstitutionSearch(newInstitutionSearch);
  };
  const handleInstitutionChange = (e: any) => {
    const newInstitutions = e.target.value as CodeBook[];
    handleInstitutionChangeNumbers(newInstitutions.map((val) => val.id));
  };
  const handleInstitutionChangeNumbers = (vals: number[]) => {
    const newInstitutionSearch: InstitutionSearch = {
      ...institutionSearch,
      institutionIds: vals,
    };
    setInstitutionSearch(newInstitutionSearch);
  };

  const handleCheckboxChange = (
    field: keyof Pick<InstitutionSearch, "isMinority" | "isReligious">,
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const newInstitutionSearch: InstitutionSearch = {
      ...institutionSearch,
      [field]: e.target.checked,
    };
    setInstitutionSearch(newInstitutionSearch);
  };

  const handleClearSearch = () => {
    const defaultInstitutionSearch: InstitutionSearch = {
      institutionIds: defaultInstitutionIds,
      programIds: [],
      townIds: [],
      excludeHandicapped: false,
      excludeSatellite: false,
      isMinority: false,
      isReligious: false,
    };

    setClearFilter(true);
    setInstitutionSearch(defaultInstitutionSearch);
    clearFilters();
    search();
  };

  return (
    <ContentArea>
      <ToolbarTop pageTitle={t("common.institutionList.institutionList")} />
      <SearchCard
        onClickSearch={search}
        onClickClearSearch={handleClearSearch}
        isFilterChanged={filterChanged}
        showSearch={
          !isInstitutionClaimType(auth.currentRole?.claimType) ||
          (institutionSearch.institutionIds &&
            institutionSearch.institutionIds.length > 0)
            ? "show"
            : "disable"
        }
      >
        {!isInstitutionClaimType(auth.currentRole?.claimType) && (
          <>
            <Row>
              <Col md={4}>
                <CountySelect
                  label={t("common.county")}
                  name={nameof<typeof institutionSearch>("countyId")}
                  value={institutionSearch.countyId}
                  onChange={handleCountyChange}
                  countyIds={
                    auth.currentRole?.claimType === ClaimType.County
                      ? auth.currentRole.locations
                      : undefined
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <AutocompleteMultipleSelect<CodeBook>
                  label={t("common.town")}
                  name={nameof<typeof institutionSearch>("townIds")}
                  value={
                    institutionSearch?.townIds &&
                    institutionSearch.townIds.length > 0
                      ? filteredTowns.filter(
                          (val) =>
                            institutionSearch.townIds.find(
                              (v) => v === val.id
                            ) !== undefined
                        )
                      : []
                  }
                  valueProp="id"
                  textProp="name"
                  onChange={handleTownChange}
                  selectOptions={filteredTowns}
                  onFilterChange={handleTownFilterChange}
                  maximumSelectedItems={10}
                />
              </Col>
              <Col md={7}>
                <AutocompleteMultipleSelect<HierarchicalCodeBook>
                  label={t("common.institution")}
                  name={nameof<typeof institutionSearch>("institutionIds")}
                  value={
                    institutionSearch.institutionIds.length > 0
                      ? filteredInstitutions.filter(
                          (val) =>
                            institutionSearch.institutionIds.find(
                              (v) => v === val.id
                            ) !== undefined
                        )
                      : []
                  }
                  valueProp="id"
                  textProp="name"
                  onChange={handleInstitutionChange}
                  selectOptions={filteredInstitutions}
                  onFilterChange={handleInstitutionFilterChange}
                  maximumSelectedItems={10}
                />
              </Col>
            </Row>
          </>
        )}
        {isInstitutionClaimType(auth.currentRole?.claimType) && (
          <Row>
            <Col md={12}>
              <Select
                multiple
                label={t("common.institution")}
                name={nameof<typeof institutionSearch>("institutionIds")}
                value={institutionSearch.institutionIds}
                onChange={(e) => handleInstitutionChangeNumbers(e.target.value)}
                selectData={codeBookToSelectData(filteredInstitutions)}
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col md={8}>
            <Select
              multiple
              label={t("common.programs")}
              name={nameof<typeof institutionSearch>("programIds")}
              value={institutionSearch.programIds}
              onChange={handleProgramChange}
              selectData={codeBookToSelectData(programs)}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <Checkbox
              label={t("common.institutionList.minority")}
              name="isMinority"
              checked={institutionSearch.isMinority}
              onChange={(e) => handleCheckboxChange("isMinority", e)}
            />
          </Col>
          <Col md={6}>
            <Checkbox
              label={t("common.institutionList.religious")}
              name="isReligious"
              checked={institutionSearch.isReligious}
              onChange={(e) => handleCheckboxChange("isReligious", e)}
            />
          </Col>
        </Row>
      </SearchCard>

      <div>
        <ListTable
          tableProps={tableProps}
          onClickDownload={(exportType: ExportTypeEnum) =>
            handleDownloadClicked(exportType)
          }
          showDownload
        />
      </div>
    </ContentArea>
  );
}
